import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { idoAPI, userAPI, notificationsAPI } from '../../services/api';
import { COLORS } from '../../utils/constants';
import { useLanguage } from '../../context/LanguageContext';
import MobileBottomNav from '../layout/MobileBottomNav';
import { Rocket, RefreshCw, ChevronRight, X, TrendingUp, Bell } from 'lucide-react';

// ==================== COLORS ====================
const C = {
  bg: '#0b0e11',
  bgCard: '#1a1d21',
  bgElevated: '#23272b',
  bgInput: '#181b1f',
  border: '#2a2e33',
  text: '#eaecef',
  textSecondary: '#848e9c',
  textMuted: '#5e6673',
  yellow: '#f0b90b',
  green: '#0ecb81',
  red: '#f6465d',
  greenBg: 'rgba(14, 203, 129, 0.1)',
  redBg: 'rgba(246, 70, 93, 0.1)',
  yellowBg: 'rgba(240, 185, 11, 0.1)',
  yellowBorder: 'rgba(240, 185, 11, 0.3)',
  redBorder: 'rgba(246, 70, 93, 0.3)',
};

// ==================== MAIN COMPONENT ====================
const MobileIDOPage = () => {
  const { t } = useLanguage();
  
  const [projects, setProjects] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [history, setHistory] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  
  const [buyModal, setBuyModal] = useState(null);
  const [sellModal, setSellModal] = useState(null);

  // Load notifications count
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const res = await notificationsAPI.getUnreadCount();
        setUnreadNotifications(res.data?.count || 0);
      } catch (err) {}
    };
    loadUnreadCount();
  }, []);

  // Load data
  const loadData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [projectsRes, holdingsRes, historyRes, balanceRes] = await Promise.all([
        idoAPI.getProjects(),
        idoAPI.getMyHoldings(),
        idoAPI.getMyHistory(),
        userAPI.getBalance(),
      ]);
      setProjects(projectsRes.data?.projects || []);
      setHoldings(holdingsRes.data?.holdings || []);
      setHistory(historyRes.data?.transactions || []);
      setBalance(balanceRes.data?.exchange_balance || 0);
    } catch (err) {
      console.error('Failed to load IDO data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Categorize projects
  const liveProjects = projects.filter(p => p.is_launched && p.buy_enabled);
  const upcomingProjects = projects.filter(p => !p.is_launched);
  const sellableProjects = projects.filter(p => {
    if (!p.is_launched || !p.sell_enabled || !p.sell_price) return false;
    const holding = holdings.find(h => h.asset === p.symbol);
    return holding && holding.quantity > 0;
  });
  const myHoldings = holdings.filter(h => h.quantity > 0);
  const portfolioValue = myHoldings.reduce((sum, h) => sum + (h.current_value || 0), 0);

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: C.bg, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <RefreshCw size={24} color={C.yellow} className="animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: C.bg, 
      paddingBottom: 90,
      paddingTop: 12,
    }}>
      {/* Balance Card */}
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ 
          background: C.bgCard,
          borderRadius: 12,
          padding: 14,
          border: `1px solid ${C.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <div style={{ color: C.textSecondary, fontSize: 11, marginBottom: 2 }}>Баланс USDT</div>
            <div style={{ color: C.text, fontSize: 20, fontWeight: 700 }}>
              ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          {myHoldings.length > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: C.textSecondary, fontSize: 11, marginBottom: 2 }}>Портфель IDO</div>
              <div style={{ color: C.yellow, fontSize: 20, fontWeight: 700 }}>
                ${portfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🟢 Открыты для покупки */}
      {liveProjects.length > 0 && (
        <Section title="🟢 Открыты для покупки" count={liveProjects.length}>
          {liveProjects.map(project => {
            const holding = holdings.find(h => h.asset === project.symbol);
            return (
              <LiveProjectCard 
                key={project.id} 
                project={project}
                holding={holding}
                onBuy={() => setBuyModal(project)}
                onSell={() => holding && setSellModal({ project, holding })}
              />
            );
          })}
        </Section>
      )}

      {/* ⏳ Скоро */}
      {upcomingProjects.length > 0 && (
        <Section title="⏳ Скоро" count={upcomingProjects.length}>
          {upcomingProjects.map(project => (
            <UpcomingProjectCard key={project.id} project={project} />
          ))}
        </Section>
      )}

      {/* 🔴 Открыты для продажи */}
      {sellableProjects.length > 0 && (
        <Section title="🔴 Открыты для продажи" count={sellableProjects.length}>
          {sellableProjects.map(project => {
            const holding = holdings.find(h => h.asset === project.symbol);
            return (
              <SellableProjectCard 
                key={project.id} 
                project={project}
                holding={holding}
                onSell={() => setSellModal({ project, holding })}
              />
            );
          })}
        </Section>
      )}

      {/* IDO Токены */}
      {myHoldings.length > 0 && (
        <Section title="IDO Токены" count={myHoldings.length}>
          {myHoldings.map((holding, idx) => {
            const project = projects.find(p => p.symbol === holding.asset);
            return (
              <HoldingCard 
                key={holding.id || idx}
                holding={holding}
                project={project}
                onSell={() => project && project.sell_enabled && setSellModal({ project, holding })}
              />
            );
          })}
        </Section>
      )}

      {/* История депозитов */}
      {history.length > 0 && (
        <Section title="История депозитов" count={history.length}>
          {history.map((tx, idx) => (
            <HistoryItem key={tx.id || idx} tx={tx} />
          ))}
        </Section>
      )}

      {/* Empty State */}
      {projects.length === 0 && myHoldings.length === 0 && (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <Rocket size={48} color={C.textMuted} style={{ marginBottom: 16 }} />
          <div style={{ color: C.textSecondary, fontSize: 14 }}>
            Нет доступных IDO проектов
          </div>
        </div>
      )}

      {/* Modals */}
      {buyModal && (
        <BuyModal 
          project={buyModal} 
          balance={balance}
          onClose={() => setBuyModal(null)}
          onSuccess={() => { setBuyModal(null); loadData(); }}
        />
      )}

      {sellModal && (
        <SellModal
          project={sellModal.project}
          holding={sellModal.holding}
          onClose={() => setSellModal(null)}
          onSuccess={() => { setSellModal(null); loadData(); }}
        />
      )}

      {/* Bottom Navigation */}
      <MobileBottomNav />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ==================== SECTION ====================
const Section = ({ title, count, children }) => (
  <div style={{ padding: '0 16px', marginBottom: 20 }}>
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 8,
      marginBottom: 10,
    }}>
      <span style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{title}</span>
      <span style={{ 
        background: C.bgElevated, 
        color: C.textSecondary, 
        fontSize: 11, 
        padding: '2px 8px', 
        borderRadius: 10 
      }}>
        {count}
      </span>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {children}
    </div>
  </div>
);

// ==================== UPCOMING PROJECT CARD ====================
const UpcomingProjectCard = ({ project }) => {
  const launchDate = new Date(project.launch_date);
  const now = new Date();
  const diff = launchDate - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  let timeText = 'Скоро';
  if (diff > 0) {
    if (days > 0) {
      timeText = `${days}д ${hours}ч`;
    } else if (hours > 0) {
      timeText = `${hours}ч ${minutes}м`;
    } else if (minutes > 0) {
      timeText = `${minutes}м`;
    }
  }

  return (
    <div style={{
      background: C.bgCard,
      borderRadius: 12,
      border: `1px solid ${C.border}`,
      padding: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      {/* Logo */}
      <div style={{
        width: 44, height: 44, borderRadius: 10, background: C.bgElevated,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {project.logo ? (
          <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
        ) : (
          <span style={{ color: C.yellow, fontWeight: 700, fontSize: 16 }}>{project.symbol?.charAt(0)}</span>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{project.name}</span>
          <span style={{ 
            background: C.bgElevated, color: C.textSecondary, fontSize: 10, fontWeight: 600, 
            padding: '2px 6px', borderRadius: 4 
          }}>
            ${project.symbol}
          </span>
        </div>
        <div style={{ color: C.textSecondary, fontSize: 12 }}>
          Цена: ${project.buy_price} • Hard cap: {project.total_supply?.toLocaleString()}
        </div>
      </div>

      {/* Countdown */}
      <div style={{
        padding: '8px 12px',
        background: C.yellowBg,
        borderRadius: 8,
        textAlign: 'center',
      }}>
        <div style={{ color: C.yellow, fontSize: 14, fontWeight: 700 }}>
          {timeText}
        </div>
      </div>
    </div>
  );
};


// ==================== LIVE PROJECT CARD ====================
const LiveProjectCard = ({ project, holding, onBuy, onSell }) => {
  const progress = ((project.sold_amount || 0) / project.total_supply) * 100;
  const remaining = project.total_supply - (project.sold_amount || 0);
  const holdingValue = holding ? holding.quantity * (project.sell_price || project.buy_price) : 0;

  return (
    <div style={{
      background: C.bgCard,
      borderRadius: 12,
      border: `1px solid ${C.yellowBorder}`,
      overflow: 'hidden',
    }}>
      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Logo */}
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: C.bgElevated,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {project.logo ? (
              <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
            ) : (
              <span style={{ color: C.yellow, fontWeight: 700, fontSize: 16 }}>{project.symbol?.charAt(0)}</span>
            )}
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{project.name}</span>
              <span style={{ 
                background: C.yellow, color: '#000', fontSize: 10, fontWeight: 700, 
                padding: '2px 6px', borderRadius: 4 
              }}>
                ${project.symbol}
              </span>
              <span style={{ fontSize: 12 }}>ℹ️</span>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div>
                <div style={{ color: C.textMuted, fontSize: 10 }}>Цена</div>
                <div style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>${project.buy_price}</div>
              </div>
              <div>
                <div style={{ color: C.textMuted, fontSize: 10 }}>Осталось</div>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>
                  {remaining >= 1000000 ? `${(remaining/1000000).toFixed(1)}M` : 
                   remaining >= 1000 ? `${Math.round(remaining/1000)}K` : remaining.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ color: C.textMuted, fontSize: 10 }}>Прогресс</div>
                <div style={{ color: C.yellow, fontSize: 13, fontWeight: 600 }}>{progress.toFixed(1)}%</div>
              </div>
            </div>
          </div>

          {/* Buy Button */}
          <button onClick={onBuy} style={{
            padding: '10px 16px', background: C.yellow, border: 'none', borderRadius: 8,
            color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>
            Купить
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{ marginTop: 10 }}>
          <div style={{ height: 4, background: C.bgElevated, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(progress, 100)}%`, background: C.yellow }} />
          </div>
        </div>
      </div>

      {/* User Holdings */}
      {holding && holding.quantity > 0 && (
        <div style={{
          padding: '10px 12px',
          background: C.greenBg,
          borderTop: `1px solid ${C.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <span style={{ color: C.textSecondary, fontSize: 12 }}>Ваш баланс: </span>
            <span style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>
              {holding.quantity.toLocaleString()} токенов
            </span>
            <span style={{ color: C.textSecondary, fontSize: 12 }}>
              {' '}(≈${holdingValue.toFixed(2)})
            </span>
          </div>
          {project.sell_enabled && project.sell_price && (
            <button onClick={onSell} style={{
              padding: '6px 12px', background: C.redBg, border: `1px solid ${C.redBorder}`,
              borderRadius: 6, color: C.red, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
              Продать
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== SELLABLE PROJECT CARD ====================
const SellableProjectCard = ({ project, holding, onSell }) => {
  const sellValue = holding.quantity * project.sell_price;

  return (
    <div style={{
      background: C.bgCard,
      borderRadius: 12,
      border: `1px solid ${C.redBorder}`,
      padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        {/* Logo */}
        <div style={{
          width: 44, height: 44, borderRadius: 10, background: C.bgElevated,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {project.logo ? (
            <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
          ) : (
            <span style={{ color: C.red, fontWeight: 700, fontSize: 16 }}>{project.symbol?.charAt(0)}</span>
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{project.name}</span>
            <span style={{ 
              background: C.red, color: '#fff', fontSize: 10, fontWeight: 700, 
              padding: '2px 6px', borderRadius: 4 
            }}>
              SELL
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', color: C.textSecondary, fontSize: 12 }}>
            <span>Баланс: <b style={{ color: C.text }}>{holding.quantity.toLocaleString()}</b></span>
            <span>Цена: <b style={{ color: C.red }}>${project.sell_price}</b></span>
          </div>
        </div>
      </div>

      {/* Bottom row with value and button */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        paddingTop: 10,
        borderTop: `1px solid ${C.border}`,
      }}>
        <div>
          <div style={{ color: C.textSecondary, fontSize: 11, marginBottom: 2 }}>К получению</div>
          <div style={{ color: C.green, fontSize: 16, fontWeight: 700 }}>${sellValue.toFixed(2)}</div>
        </div>
        <button onClick={onSell} style={{
          padding: '10px 20px', background: C.red, border: 'none', borderRadius: 8,
          color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
        }}>
          Продать
        </button>
      </div>
    </div>
  );
};

// ==================== HOLDING CARD ====================
const HoldingCard = ({ holding, project, onSell }) => {
  const canSell = project?.sell_enabled && project?.sell_price;
  const currentPrice = project?.sell_price || project?.buy_price || holding.avg_buy_price || 0;
  const value = holding.quantity * currentPrice;
  const invested = holding.quantity * (holding.avg_buy_price || 0);
  const pnl = value - invested;
  const pnlPercent = invested > 0 ? (pnl / invested * 100) : 0;
  const isProfit = pnl >= 0;

  return (
    <div style={{
      background: C.bgCard,
      borderRadius: 12,
      border: `1px solid ${C.border}`,
      padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Logo */}
        <div style={{
          width: 40, height: 40, borderRadius: 8, background: C.bgElevated,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {holding.logo ? (
            <img src={holding.logo} alt={holding.asset} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
          ) : (
            <span style={{ color: C.yellow, fontWeight: 700, fontSize: 14 }}>{holding.asset?.charAt(0)}</span>
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{holding.token_name || project?.name || holding.asset}</span>
            <span style={{ color: C.textSecondary, fontSize: 11 }}>${holding.asset}</span>
          </div>
          <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>
            Куплено по ${holding.avg_buy_price?.toFixed(2) || '0'}
          </div>
        </div>

        {/* Amount & Value */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>
            {holding.quantity.toLocaleString()}
          </div>
          <div style={{ color: C.textSecondary, fontSize: 11 }}>
            ≈${value.toFixed(2)}
          </div>
        </div>

        {/* Sell Button */}
        {canSell && (
          <button onClick={onSell} style={{
            padding: '8px 12px', background: C.redBg, border: `1px solid ${C.redBorder}`,
            borderRadius: 6, color: C.red, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            flexShrink: 0,
          }}>
            Продать
          </button>
        )}
      </div>

      {/* PnL Row */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginTop: 10,
        paddingTop: 10,
        borderTop: `1px solid ${C.border}`,
      }}>
        <div style={{ color: C.textSecondary, fontSize: 11 }}>
          Инвестировано: <span style={{ color: C.text }}>${invested.toFixed(2)}</span>
        </div>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 6,
          padding: '4px 8px',
          borderRadius: 6,
          background: isProfit ? C.greenBg : C.redBg,
        }}>
          <span style={{ 
            color: isProfit ? C.green : C.red, 
            fontSize: 12, 
            fontWeight: 700 
          }}>
            {isProfit ? '+' : ''}{pnl.toFixed(2)} $
          </span>
          <span style={{ 
            color: isProfit ? C.green : C.red, 
            fontSize: 11,
          }}>
            ({isProfit ? '+' : ''}{pnlPercent.toFixed(1)}%)
          </span>
        </div>
      </div>
    </div>
  );
};

// ==================== HISTORY ITEM ====================
const HistoryItem = ({ tx }) => {
  const isBuy = tx.type === 'buy';
  const date = new Date(tx.created_at);
  const dateStr = date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) + 
                  ' ' + date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{
      background: C.bgCard,
      borderRadius: 10,
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      {/* Icon */}
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        background: isBuy ? C.greenBg : C.redBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <TrendingUp size={14} color={isBuy ? C.green : C.red} style={{ transform: isBuy ? 'none' : 'rotate(180deg)' }} />
      </div>

      {/* Info */}
      <div style={{ flex: 1 }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 500 }}>
          {isBuy ? 'Покупка' : 'Продажа'} ${tx.ido_symbol}
        </div>
        <div style={{ color: C.textMuted, fontSize: 11 }}>{dateStr}</div>
      </div>

      {/* Amount */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: isBuy ? C.green : C.red, fontSize: 13, fontWeight: 600 }}>
          {isBuy ? '+' : '-'}{tx.token_amount?.toLocaleString()} токенов
        </div>
        <div style={{ color: C.textMuted, fontSize: 11 }}>
          {isBuy ? '-' : '+'}${tx.usdt_amount?.toFixed(2)}
        </div>
      </div>
    </div>
  );
};

// ==================== BUY MODAL ====================
const BuyModal = ({ project, balance, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const usdtAmount = parseFloat(amount) || 0;
  const tokenAmount = usdtAmount / project.buy_price;
  const remaining = project.total_supply - (project.sold_amount || 0);
  const maxPurchase = Math.min(balance, project.max_purchase || Infinity, remaining * project.buy_price);

  const handleBuy = async () => {
    if (usdtAmount <= 0) { setError('Введите сумму'); return; }
    if (usdtAmount > balance) { setError('Недостаточно средств'); return; }
    if (project.min_purchase && usdtAmount < project.min_purchase) { setError(`Минимум: $${project.min_purchase}`); return; }

    setLoading(true);
    setError('');
    try {
      await idoAPI.buyTokens(project.id, usdtAmount);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка покупки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      <div style={{ padding: 16 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: C.bgElevated,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {project.logo ? (
              <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
            ) : (
              <span style={{ color: C.yellow, fontWeight: 700, fontSize: 16 }}>{project.symbol?.charAt(0)}</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>Купить ${project.symbol}</div>
            <div style={{ color: C.textSecondary, fontSize: 12 }}>${project.buy_price} за токен</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer' }}>
            <X size={20} color={C.textSecondary} />
          </button>
        </div>

        {error && (
          <div style={{ padding: 10, background: C.redBg, borderRadius: 8, color: C.red, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* Amount Input */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ color: C.textSecondary, fontSize: 12 }}>Сумма USDT</span>
            <span style={{ color: C.textMuted, fontSize: 11 }}>Баланс: ${balance.toFixed(2)}</span>
          </div>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              style={{
                width: '100%', padding: '14px', paddingRight: 60, background: C.bgInput,
                border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
                fontSize: 18, fontWeight: 600, boxSizing: 'border-box',
              }}
            />
            <button
              onClick={() => setAmount(maxPurchase.toFixed(2))}
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                padding: '6px 10px', background: C.yellow, border: 'none', borderRadius: 6,
                color: '#000', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}
            >
              MAX
            </button>
          </div>
        </div>

        {/* Result */}
        <div style={{ padding: 14, background: C.bgElevated, borderRadius: 10, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: C.textSecondary, fontSize: 12 }}>Вы получите</span>
            <span style={{ color: C.yellow, fontSize: 16, fontWeight: 700 }}>
              {tokenAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${project.symbol}
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 14, background: C.bgElevated, border: `1px solid ${C.border}`,
            borderRadius: 10, color: C.textSecondary, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>
            Отмена
          </button>
          <button onClick={handleBuy} disabled={loading || usdtAmount <= 0} style={{
            flex: 2, padding: 14, background: loading ? C.bgElevated : C.yellow, border: 'none',
            borderRadius: 10, color: loading ? C.textSecondary : '#000', fontSize: 14, fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}>
            {loading ? 'Загрузка...' : 'Купить'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

// ==================== SELL MODAL ====================
const SellModal = ({ project, holding, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const tokenAmount = parseFloat(amount) || 0;
  const usdtAmount = tokenAmount * project.sell_price;
  const maxSell = holding?.quantity || 0;

  const handleSell = async () => {
    if (tokenAmount <= 0) { setError('Введите количество'); return; }
    if (tokenAmount > maxSell) { setError('Недостаточно токенов'); return; }

    setLoading(true);
    setError('');
    try {
      await idoAPI.sellTokens(project.id, tokenAmount);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка продажи');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      <div style={{ padding: 16 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: C.bgElevated,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {project.logo ? (
              <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
            ) : (
              <span style={{ color: C.red, fontWeight: 700, fontSize: 16 }}>{project.symbol?.charAt(0)}</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>Продать ${project.symbol}</div>
            <div style={{ color: C.textSecondary, fontSize: 12 }}>${project.sell_price} за токен</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer' }}>
            <X size={20} color={C.textSecondary} />
          </button>
        </div>

        {error && (
          <div style={{ padding: 10, background: C.redBg, borderRadius: 8, color: C.red, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* Amount Input */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ color: C.textSecondary, fontSize: 12 }}>Количество</span>
            <span style={{ color: C.textMuted, fontSize: 11 }}>Баланс: {maxSell.toLocaleString()}</span>
          </div>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              style={{
                width: '100%', padding: '14px', paddingRight: 60, background: C.bgInput,
                border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
                fontSize: 18, fontWeight: 600, boxSizing: 'border-box',
              }}
            />
            <button
              onClick={() => setAmount(maxSell.toString())}
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                padding: '6px 10px', background: C.red, border: 'none', borderRadius: 6,
                color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}
            >
              ВСЁ
            </button>
          </div>
        </div>

        {/* Result */}
        <div style={{ padding: 14, background: C.bgElevated, borderRadius: 10, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: C.textSecondary, fontSize: 12 }}>Вы получите</span>
            <span style={{ color: C.green, fontSize: 16, fontWeight: 700 }}>
              ${usdtAmount.toFixed(2)} USDT
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 14, background: C.bgElevated, border: `1px solid ${C.border}`,
            borderRadius: 10, color: C.textSecondary, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>
            Отмена
          </button>
          <button onClick={handleSell} disabled={loading || tokenAmount <= 0} style={{
            flex: 2, padding: 14, background: loading ? C.bgElevated : C.red, border: 'none',
            borderRadius: 10, color: loading ? C.textSecondary : '#fff', fontSize: 14, fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}>
            {loading ? 'Загрузка...' : 'Продать'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

// ==================== MODAL WRAPPER ====================
const ModalWrapper = ({ children, onClose }) => (
  <div 
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
      zIndex: 10000,
    }}
    onClick={onClose}
  >
    <div 
      onClick={(e) => e.stopPropagation()}
      style={{
        background: C.bgCard, borderRadius: '16px 16px 0 0',
        width: '100%', maxHeight: '90vh', overflow: 'auto',
      }}
    >
      {children}
    </div>
  </div>
);

export default MobileIDOPage;
